import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeAlert, ChevronDown, Menu } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { api } from "../lib/api";
import { startAutoSync, useOfflineStatus, useOutboxItems } from "../offline/runtime";
import { warmOfflineCache } from "../offline/warmCache";
import type { AttentionData } from "../types";
import { buildBadges, isMenuItemActive, visibleGroups } from "./navigation";
import { InstallPrompt } from "./InstallPrompt";
import { OfflineStatus } from "./OfflineStatus";
import { BottomNav, MobileDrawer, Sidebar } from "./ShellNav";

export { isMenuItemActive };

const roleLabel = { admin: "Administrador", inspector: "Fiscalizador" } as const;

export function AppLayout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { online } = useOfflineStatus();
  const { items: pendingItems } = useOutboxItems(user?.id);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // Vencimentos de documentos e credenciais: alimentam os selos do menu e o aviso fixo do topo.
  const attention = useQuery({ queryKey: ["attention"], queryFn: () => api<AttentionData>("/attention"), enabled: Boolean(user), staleTime: 5 * 60_000, retry: false });
  const groups = useMemo(() => visibleGroups(user!.role), [user]);
  const badges = useMemo(() => buildBadges({
    pending: pendingItems.length,
    documents: attention.data && { expired: attention.data.documents.expiredDocuments, expiring: attention.data.documents.expiringDocuments },
    credentials: attention.data && { expired: attention.data.credentials.expired, expiring: attention.data.credentials.expiring }
  }), [pendingItems.length, attention.data]);
  const expiredCredentials = attention.data?.credentials.expired || 0;

  useEffect(() => { setDrawerOpen(false); setProfileOpen(false); }, [location.pathname]);
  // Envio automático das vistorias guardadas no aparelho e download dos cadastros para uso sem internet.
  useEffect(() => (user ? startAutoSync(user.id, queryClient) : undefined), [user?.id, queryClient]);
  useEffect(() => { if (user && online) void warmOfflineCache(queryClient); }, [user?.id, online, queryClient]);

  const handleSignOut = () => {
    if (pendingItems.length && !window.confirm(`Há ${pendingItems.length} vistoria(s) neste aparelho que ainda não foram enviadas. Elas continuam guardadas, mas só serão enviadas quando você entrar de novo com este usuário e houver internet. Deseja sair mesmo assim?`)) return;
    void signOut();
  };
  const canCreate = groups.some((group) => group.items.some((item) => item.to === "/vistorias/nova"));
  const menuBadge = badges["/credenciais"]?.tone === "danger" ? badges["/credenciais"] : undefined;

  return <div className="app-shell cs-shell">
    <Sidebar groups={groups} pathname={location.pathname} badges={badges} onSignOut={handleSignOut}/>
    <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} groups={groups} pathname={location.pathname} badges={badges} onSignOut={handleSignOut}/>
    <div className="cs-main">
      <header className="cs-topbar">
        <button type="button" className="cs-menu-btn" onClick={() => setDrawerOpen(true)} aria-label="Abrir menu"><Menu size={22} aria-hidden="true"/></button>
        <img className="cs-topbar-mark" src="/assets/icon-white.png" alt="" aria-hidden="true"/>
        <div className="cs-topbar-title"><strong>Meta Gestão Veicular</strong><span>Controle operacional e conformidade</span></div>
        {expiredCredentials > 0 && <Link className="cs-alert-chip" to="/credenciais" role="alert" aria-label={`${expiredCredentials} credencial(is) vencida(s). Abrir credenciais`}>
          <BadgeAlert size={16} aria-hidden="true"/><span>{expiredCredentials === 1 ? "1 credencial vencida" : `${expiredCredentials} credenciais vencidas`}</span>
        </Link>}
        <div className="user-menu">
          <button type="button" onClick={() => setProfileOpen(!profileOpen)} aria-expanded={profileOpen} aria-haspopup="menu">
            <span className="avatar">{user!.name.slice(0, 1)}</span>
            <span className="user-copy"><strong>{user!.name}</strong><small>{roleLabel[user!.role]}</small></span>
            <ChevronDown size={16} aria-hidden="true"/>
          </button>
          {profileOpen && <div className="user-popover"><span>{user!.email}</span><button type="button" onClick={handleSignOut}>Sair</button></div>}
        </div>
      </header>
      <InstallPrompt/>
      <OfflineStatus/>
      <main className="page"><Outlet/></main>
    </div>
    <BottomNav pathname={location.pathname} canCreate={canCreate} badges={badges} menuBadge={menuBadge} onOpenMenu={() => setDrawerOpen(true)}/>
  </div>;
}
